// File upload types for unified API

// Backend-detected file types
export type DetectedFileType = 'tms' | 'vehicle_metrics' | 'transport_order_add' | 'cpi' | 'fuel' | 'hoc' | 'unknown';

// Detection confidence levels
export type DetectionConfidence = 'high' | 'medium' | 'low';

// Module types
export type FileModule = 'LPI' | 'CPI';

// File detection result from /upload/detect endpoint
export interface FileDetectionResult {
  type: DetectedFileType;
  confidence: DetectionConfidence;
  sheetName?: string;
  detectedColumns?: string[];
  message: string;
}

// Upload file metadata with detection info
export interface UploadFileMetadata {
  file: File;
  detection?: FileDetectionResult;
  selectedType?: DetectedFileType; // User-selected type (overrides detected type for display)
  isDetecting?: boolean;
  detectionError?: string;
}

// Upload progress tracking
export interface UploadProgress {
  current: number;
  total: number;
  currentFileName: string;
}

// Upload options for the unified API
export interface UploadOptions {
  checkDuplicate?: boolean;
  force?: boolean;
  replace?: boolean;
  autoRecalculateCpi?: boolean;
}

// Processing status from upload response
export interface ProcessingStatus {
  status: 'queued' | 'processing' | 'completed' | 'failed';
  queuedAt?: string;
  startedAt?: string;
  completedAt?: string;
}

// Upload response from /upload endpoint
export interface UploadResponse {
  success: boolean;
  fileType: DetectedFileType;
  filename: string;
  fileHash: string;
  detection: {
    confidence: DetectionConfidence;
    message: string;
  };
  result: UploadResult;
  processing?: ProcessingStatus;
  message?: string;
}

// Result types for different file types
export interface TMSResult {
  message: string;
  summary: {
    total_orders: number;
    successful: number;
    failed: number;
    incomplete_add: number;
    total_shipments: number;
    total_add_km: number;
    total_weight_tons: number;
  };
  orders?: unknown[];
  errors?: unknown[];
  warnings?: Record<string, unknown>;
}

export interface VehicleMetricsResult {
  total: number;
  successful: number;
  failed: number;
  errors: unknown[];
}

export interface TransportOrderAddResult {
  total: number;
  successful: number;
  failed: number;
  errors: unknown[];
}

export interface CPIResult {
  uploadId: string;
  rowsProcessed: number;
  carriersAffected: number;
}

export interface FuelResult {
  filename: string;
  totalRows: number;
  insertedRows: number;
  updatedRows: number;
  skippedRows: number;
  uniqueVehicles: number;
  uniqueFuelTypes: string[];
  dateRange: {
    start: string;
    end: string;
  };
  warnings: unknown[];
}

export type UploadResult = TMSResult | VehicleMetricsResult | TransportOrderAddResult | CPIResult | FuelResult;

// Supported file type info from /upload/types endpoint
export interface SupportedFileType {
  type: DetectedFileType;
  name: string;
  description: string;
  requiredColumns: string[];
  module: FileModule;
}

// Labels for file types
export const FILE_TYPE_LABELS: Record<DetectedFileType, string> = {
  tms: 'TMS Transport Order',
  vehicle_metrics: 'Vehicle Metrics',
  transport_order_add: 'Transport Order ADD',
  cpi: 'CPI Indicator',
  fuel: 'Fuel Consumption',
  hoc: 'HOC (Hub of Consolidation)',
  unknown: 'Unknown',
};

// Module labels for file types
export const FILE_TYPE_MODULES: Record<DetectedFileType, FileModule | null> = {
  tms: 'LPI',
  vehicle_metrics: 'LPI',
  transport_order_add: 'LPI',
  cpi: 'CPI',
  fuel: 'CPI',
  hoc: 'CPI',
  unknown: null,
};

// Confidence level colors/styles
export const CONFIDENCE_STYLES: Record<DetectionConfidence, { color: string; bgColor: string }> = {
  high: { color: '#059669', bgColor: '#ecfdf5' },
  medium: { color: '#d97706', bgColor: '#fffbeb' },
  low: { color: '#dc2626', bgColor: '#fef2f2' },
};

// Legacy FileType enum for backward compatibility with LPI Reports
export enum FileType {
  TMS = 'tms',
  TRANSPORT_ORDER = 'transport_order',
  ADD = 'add',
  UNKNOWN = 'unknown',
}

// Legacy upload file metadata for LPI Reports modal (backward compatibility)
export interface LegacyUploadFileMetadata {
  file: File;
  type: FileType;
  detectedType: FileType;
}
